import { User2 } from "lucide-react";
import { Img } from 'openimg/react'
import * as React from "react";
import { getCdnUserImgSrc, getUserImgSrc } from "#app/utils/misc.tsx";

// Define the types used within the component
type ThreadMessage = {
  id: string;
  message: string;
  createdAt: string;
  user: {
    username: string | null;
    image: {
      id: string;
      objectKey: string;
    } | null;
  } | null;
};

type ThreadMessagesResponse = {
  messages: ThreadMessage[];
  error?: string;
};

// Type guard function
function isThreadMessagesResponse(
  data: unknown,
): data is ThreadMessagesResponse {
  return (
    typeof data === "object" &&
    data !== null &&
    "messages" in data &&
    Array.isArray((data as any).messages)
  );
}

interface ThreadMessagesProps {
  reviewId: number;
  review: any; // Consider defining a more specific type for review
}

export function ThreadMessages({ reviewId, review }: ThreadMessagesProps) {
  const [messages, setMessages] = React.useState<ThreadMessage[]>(
    review.threadMessages || [],
  );
  const containerRef = React.useRef<HTMLDivElement>(null);

  // Listen for custom refresh events for updates after posting a new message
  React.useEffect(() => {
    const handleRefresh = (event: CustomEvent<{ reviewId: number }>) => {
      if (event.detail && event.detail.reviewId === reviewId) {
        // Fetch fresh data after a new message is posted
        fetch(`/api/thread-messages?reviewId=${reviewId}`)
          .then((response) => response.json())
          .then((data: unknown) => {
            if (isThreadMessagesResponse(data)) {
              setMessages(data.messages);

              // Scroll to the bottom when messages update
              setTimeout(() => {
                if (containerRef.current) {
                  containerRef.current.scrollTop =
                    containerRef.current.scrollHeight;
                }
              }, 100);
            }
          })
          .catch((error) => console.error("Error refreshing messages:", error));
      }
    };

    // Add event listener
    document.addEventListener(
      "refreshThreadMessages",
      handleRefresh as EventListener,
    );

    // Clean up
    return () => {
      document.removeEventListener(
        "refreshThreadMessages",
        handleRefresh as EventListener,
      );
    };
  }, [reviewId]);

  // Scroll to bottom on initial load
  React.useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, []);

  return (
    <div
      ref={containerRef}
      data-messages-container={reviewId}
      className="space-y-5 max-h-[300px] overflow-y-auto pr-2"
    >
      {!messages.length ? (
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <p className="text-sm text-muted-foreground">
            Nenhuma resposta nesta conversa ainda.
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Seja o primeiro a responder!
          </p>
        </div>
      ) : (
        messages.map((message, index) => (
          <div
            key={message.id}
            className="flex gap-3 animate-in fade-in slide-in-from-bottom-3 duration-300"
            style={{ animationDelay: `${index * 100}ms` }}
          >
            <div className="flex-shrink-0">
              {message.user?.image?.id ? (
                <Img
                  src={`${getCdnUserImgSrc(
                    message.user.image.objectKey,
                  )}`}
                  width={40}
                  height={40}
                  alt="user avatar"
                  className="h-9 w-9 rounded-full object-cover border border-gray-200 dark:border-gray-600 shadow-sm"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100 border border-gray-200 text-gray-400 dark:bg-gray-700 dark:border-gray-600 shadow-sm">
                  <User2 className="h-8 w-8 rounded-md text-gray-400" />
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-sm font-semibold">
                  {message.user?.username || "Usuário"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {message.createdAt}
                </p>
              </div>
              <div className="mt-1.5 rounded-lg bg-gray-50 p-3 dark:bg-gray-800/70 shadow-sm">
                <p className="text-sm leading-relaxed">{message.message}</p>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}
